import React, { useState, useMemo } from 'react';
import DateField from '../Shared/DateField';
import { FilterPill } from '../Shared/FilterPill';
import './Tracker.css';

/* ── Obiectele recursului ── */
const OBIECTE = {
  marca_ue: {
    label: 'Marca UE',
    temei: 'art. 66–72 RMUE',
    numarLabel: 'Nr. marca / cerere',
    numarPlaceholder: '018123456',
    decizii: [
      ['examinare', 'Decizie de respingere pe motive absolute',                  true],
      ['opozitie',  'Decizie a diviziei de opozitie',                            false],
      ['anulare',   'Decizie a diviziei de anulare — decadere sau nulitate',     false],
      ['inscrieri', 'Decizie privind inscrieri, transferuri sau restitutio',     true],
    ],
  },
  desen_ue: {
    label: 'Desen UE',
    temei: 'art. 55–61 din Regulamentul 6/2002',
    numarLabel: 'Nr. desen',
    numarPlaceholder: '0161234567-0001',
    decizii: [
      ['examinare', 'Decizie de respingere a cererii',            true],
      ['nulitate',  'Decizie a diviziei de anulare — nulitate',   false],
      ['inscrieri', 'Decizie privind inscrieri sau transferuri',  true],
    ],
  },
};

/* ── Tarife (EUR, Anexa I RMUE) ── */
const TAXE_DEFAULT = {
  recurs:           720,   // taxa de recurs, marci si desene
  plafon_recurs:    550,   // plafon cheltuieli de reprezentare in recurs
  plafon_opozitie:  300,
  plafon_anulare:   450,
  restitutio:       200,
  continuare:       400,
};

const TERMENE_DEFAULT = {
  declaratie_luni:       2,   // declaratia de recurs si plata taxei
  memoriu_luni:          4,   // memoriul de motivare
  revizuire_luni:        1,   // revizuire interlocutorie, doar in proceduri unilaterale
  observatii_luni:       2,   // observatiile intimatului
  tribunal_luni:         2,   // actiune la Tribunalul UE
  tribunal_zile_distanta:10,
  cjue_luni:             2,   // recurs la Curtea de Justitie
};

const SOLUTII_CAMERA = [
  ['',            '— pe rol —'],
  ['admis',       'Recurs admis — decizie anulata'],
  ['admis_partial','Recurs admis in parte'],
  ['respins',     'Recurs respins'],
  ['trimis',      'Cauza trimisa spre reexaminare primei instante'],
  ['revizuire',   'Solutionat prin revizuire interlocutorie'],
  ['retras',      'Recurs retras'],
  ['inadmisibil', 'Recurs inadmisibil'],
];

const SOLUTII_INSTANTA = [
  ['',             '— pe rol —'],
  ['admisa',       'Actiune admisa — decizie anulata'],
  ['admisa_partial','Actiune admisa in parte'],
  ['respinsa',     'Actiune respinsa'],
  ['neadmis',      'Recurs neadmis in procedura de filtrare'],
  ['renuntare',    'Renuntare la judecata'],
];

/* ── Helpers ── */
const todayMid = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };
const fmtRO = (d) => d ? d.toLocaleDateString('ro-RO', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—';
const daysLeft = (d) => d ? Math.ceil((d - todayMid()) / 86400000) : null;
const fmtEur = (n) => Math.round(Number(n)).toLocaleString('ro-RO') + ' €';

let _nextId = 0;
const newEventId = () => `ev_${Date.now()}_${_nextId++}`;

const esteUnilateral = (r) => {
  const cfg = OBIECTE[r.obiect];
  const dec = cfg.decizii.find(([k]) => k === r.tipDecizie);
  return dec ? dec[2] : false;
};

export function emptyRecord() {
  return {
    obiect: 'marca_ue',
    pozitie: 'apelant',          // apelant | intimat
    denumire: '',
    numarDosar: '',
    client: '',
    adversar: '',
    tipDecizie: '',
    numarDecizie: '',
    dataNotificare: '',
    declaratieDepusa: false,
    dataDeclaratie: '',
    numarRecurs: '',             // R xxxx/2026-x
    taxaPlatita: false,
    memoriuDepus: false,
    dataMemoriu: '',
    revizuireInterlocutorie: false,
    observatiiIntimatDepuse: false,
    dataObservatii: '',
    recursIncident: false,
    cameraExtinsa: false,
    dataDecizieCamera: '',       // data notificarii deciziei camerei
    solutieCamera: '',
    cheltuieliInSarcina: '',
    // Tribunalul Uniunii Europene
    tribunalSesizat: false,
    dataActiuneTribunal: '',
    numarCauzaT: '',
    evenimente: [],              // [{ id, data, nota }]
    solutieTribunal: '',
    dataHotarareTribunal: '',
    // Curtea de Justitie
    cjueSesizata: false,
    dataRecursCJUE: '',
    numarCauzaC: '',
    admisFiltrare: false,
    solutieCJUE: '',
    observatii: '',
  };
}

/* ── Status ── */
function deriveStatus(r, TM) {
  const cfg = OBIECTE[r.obiect];
  const notif = parseISO(r.dataNotificare);
  const decl = parseISO(r.dataDeclaratie);
  const mem = parseISO(r.dataMemoriu);
  const decCam = parseISO(r.dataDecizieCamera);
  const hotTrib = parseISO(r.dataHotarareTribunal);
  const unilateral = esteUnilateral(r);

  const termenDeclaratie = notif ? addMonths(notif, TM.declaratie_luni) : null;
  const termenMemoriu = notif ? addMonths(notif, TM.memoriu_luni) : null;
  const termenRevizuire = mem ? addMonths(mem, TM.revizuire_luni) : null;
  const termenObservatii = mem ? addMonths(mem, TM.observatii_luni) : null;
  const termenTribunal = decCam ? addDays(addMonths(decCam, TM.tribunal_luni), TM.tribunal_zile_distanta) : null;
  const termenCJUE = hotTrib ? addDays(addMonths(hotTrib, TM.cjue_luni), TM.tribunal_zile_distanta) : null;

  const viitoare = (r.evenimente || [])
    .map(t => ({ ...t, d: parseISO(t.data) }))
    .filter(t => t.d && t.d >= todayMid())
    .sort((a, b) => a.d - b.d);
  const urmatorul = viitoare[0] || null;

  const steps = [
    { key:'decizie',    label:'Decizie notificata',          done: !!notif,                                       date: notif },
    { key:'declaratie', label:'Declaratie + taxa',           done: r.declaratieDepusa && r.taxaPlatita,           date: decl || termenDeclaratie },
    { key:'memoriu',    label:'Memoriu (4 luni)',            done: r.memoriuDepus,                                date: mem || termenMemoriu },
    { key:'raspuns',    label: unilateral ? 'Revizuire interlocutorie' : 'Observatii intimat',
      done: unilateral ? true : r.observatiiIntimatDepuse,
      date: unilateral ? termenRevizuire : termenObservatii },
    { key:'camera',     label:'Decizia camerei',             done: !!decCam,                                      date: decCam },
    { key:'tribunal',   label:'Tribunalul UE',               done: !!r.solutieTribunal,                           date: r.tribunalSesizat ? parseISO(r.dataActiuneTribunal) : termenTribunal },
    { key:'cjue',       label:'Curtea de Justitie',          done: !!r.solutieCJUE,                               date: r.cjueSesizata ? parseISO(r.dataRecursCJUE) : termenCJUE },
  ];

  let alert;
  if (!notif) {
    alert = { label:'De completat data notificarii deciziei', sev:'neutral' };
  } else if (!r.declaratieDepusa) {
    alert = { label:'Declaratia de recurs si taxa — ambele in 2 luni', date: termenDeclaratie, sev:'act' };
  } else if (!r.taxaPlatita) {
    alert = { label:'Taxa de recurs trebuie primita de Oficiu in acelasi termen de 2 luni', date: termenDeclaratie, sev:'act' };
  } else if (!r.memoriuDepus) {
    alert = { label:'Memoriul de motivare', date: termenMemoriu, sev:'act' };
  } else if (unilateral && !decCam && termenRevizuire && todayMid() < termenRevizuire) {
    alert = { label:'Posibila revizuire interlocutorie de catre prima instanta', date: termenRevizuire, sev:'watch' };
  } else if (!unilateral && !r.observatiiIntimatDepuse) {
    alert = {
      label: r.pozitie === 'intimat' ? 'De depus observatiile la memoriul de recurs' : 'Termen pentru observatiile partii adverse',
      date: termenObservatii,
      sev: r.pozitie === 'intimat' ? 'act' : 'watch',
    };
  } else if (!decCam) {
    alert = { label: r.cameraExtinsa ? 'Pe rol la Camera extinsa de recurs' : 'Pe rol la camera de recurs', sev:'info' };
  } else if (!r.tribunalSesizat && termenTribunal && todayMid() <= termenTribunal) {
    alert = { label:'Termen pentru actiunea la Tribunalul UE', date: termenTribunal, sev:'act' };
  } else if (r.tribunalSesizat && !r.solutieTribunal) {
    alert = urmatorul
      ? { label:'Termen procedural la Tribunalul UE', date: urmatorul.d, sev:'watch' }
      : { label:'Pe rol la Tribunalul UE', sev:'info' };
  } else if (r.solutieTribunal && !r.cjueSesizata && termenCJUE && todayMid() <= termenCJUE) {
    alert = { label:'Termen pentru recursul la Curtea de Justitie', date: termenCJUE, sev:'act' };
  } else if (r.cjueSesizata && !r.solutieCJUE) {
    alert = { label: r.admisFiltrare ? 'Recurs admis in principiu — pe rol la CJUE' : 'In procedura de admitere in principiu a recursului', sev:'info' };
  } else {
    alert = { label:'Dosar inchis', sev:'done' };
  }

  if (alert.date) {
    const dl = daysLeft(alert.date);
    alert.daysLeft = dl;
    if (dl < 0 && alert.sev === 'act') alert.sev = 'overdue';
  }

  return { cfg, unilateral, steps, alert, notif, decCam, termenDeclaratie, termenMemoriu, termenRevizuire, termenObservatii, termenTribunal, termenCJUE, urmatorul };
}

function calcTaxe(r, T) {
  const propria = r.pozitie === 'apelant' ? T.recurs : 0;
  const expunere = T.plafon_recurs + (r.pozitie === 'intimat' ? T.recurs : 0);
  return { recurs: T.recurs, propria, expunere };
}

/* ════════════════════════ Component ════════════════════════ */
export default function CaiAtacEUIPOTracker({ data, onChange }) {
  const [r, setR] = useState(data);
  const [taxe, setTaxe] = useState(TAXE_DEFAULT);
  const [termene, setTermene] = useState(TERMENE_DEFAULT);
  const [showSetari, setShowSetari] = useState(false);

  const set = (patch) => {
    const next = { ...r, ...patch };
    setR(next);
    onChange(next);
  };

  const status = useMemo(() => deriveStatus(r, termene), [r, termene]);
  const taxeCalc = useMemo(() => calcTaxe(r, taxe), [r, taxe]);
  const cfg = status.cfg;

  const schimbaObiect = (v) => {
    const nou = OBIECTE[v];
    set({ obiect: v, tipDecizie: '' });
  };

  const addEveniment = () => set({ evenimente: [...(r.evenimente || []), { id: newEventId(), data:'', nota:'' }] });
  const setEveniment = (id, patch) => set({ evenimente: r.evenimente.map(e => e.id === id ? { ...e, ...patch } : e) });
  const delEveniment = (id) => set({ evenimente: r.evenimente.filter(e => e.id !== id) });

  return (
    <div className="tracker">
      <AlertBanner status={status} />
      <Timeline steps={status.steps} />

      {/* Identificare */}
      <TrackerCard title="Dosarul" badge={cfg.temei}>
        <div className="f-row">
          <div className="field">
            <label>Obiectul dreptului</label>
            <FilterPill value={r.obiect} onChange={e => schimbaObiect(e.target.value)}>
              {Object.entries(OBIECTE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </FilterPill>
          </div>
          <div className="field">
            <label>Pozitia in procedura</label>
            <SegControl value={r.pozitie} onChange={v => set({ pozitie: v })}
              options={[['apelant','Apelant (formulam recurs)'],['intimat','Intimat (recurs impotriva noastra)']]} />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Denumire</label>
            <input type="text" value={r.denumire} onChange={e => set({ denumire: e.target.value })} />
          </div>
          <div className="field">
            <label>{cfg.numarLabel}</label>
            <input type="text" value={r.numarDosar} onChange={e => set({ numarDosar: e.target.value })} placeholder={cfg.numarPlaceholder} className="mono" />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Client</label>
            <input type="text" value={r.client} onChange={e => set({ client: e.target.value })} />
          </div>
          <div className="field">
            <label>Adversar</label>
            <input type="text" value={r.adversar} onChange={e => set({ adversar: e.target.value })} />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Decizia atacata</label>
            <FilterPill value={r.tipDecizie} onChange={e => set({ tipDecizie: e.target.value })}>
              <option value="">— Selecteaza —</option>
              {cfg.decizii.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </FilterPill>
          </div>
          <div className="field">
            <label>Nr. decizie</label>
            <input type="text" value={r.numarDecizie} onChange={e => set({ numarDecizie: e.target.value })} className="mono" />
          </div>
        </div>
        <div className="field">
          <label>Data notificarii deciziei</label>
          <DateField value={r.dataNotificare} onChange={v => set({ dataNotificare: v })} />
          {status.termenDeclaratie && (
            <span className="f-hint">Termen declaratie + taxa: {fmtRO(status.termenDeclaratie)} ({daysLeft(status.termenDeclaratie)} zile)</span>
          )}
        </div>
      </TrackerCard>

      {/* Camera de recurs */}
      <TrackerCard title="Camera de recurs EUIPO">
        <div className="f-row">
          <div className="field">
            <label>Nr. recurs</label>
            <input type="text" value={r.numarRecurs} onChange={e => set({ numarRecurs: e.target.value })} placeholder="R 0123/2026-1" className="mono" />
          </div>
          <div className="field">
            <label>Data declararii recursului</label>
            <DateField value={r.dataDeclaratie} onChange={v => set({ dataDeclaratie: v })} />
          </div>
        </div>
        {status.termenDeclaratie && <DeadlineChip label={`Termen declaratie + taxa (${termene.declaratie_luni} luni)`} date={status.termenDeclaratie} act={!r.declaratieDepusa} />}

        <label className="check-label mt8">
          <input type="checkbox" checked={r.declaratieDepusa} onChange={e => set({ declaratieDepusa: e.target.checked })} />
          Declaratia de recurs a fost depusa
        </label>
        <label className="check-label">
          <input type="checkbox" checked={r.taxaPlatita} onChange={e => set({ taxaPlatita: e.target.checked })} />
          Taxa de recurs ({taxe.recurs} €) a fost platita
        </label>

        <div className="tr-divider mt8">Memoriu</div>
        <div className="field">
          <label>Data depunerii memoriului</label>
          <DateField value={r.dataMemoriu} onChange={v => set({ dataMemoriu: v })} />
        </div>
        {status.termenMemoriu && <DeadlineChip label={`Termen memoriu (${termene.memoriu_luni} luni)`} date={status.termenMemoriu} act={!r.memoriuDepus} />}
        <label className="check-label mt4">
          <input type="checkbox" checked={r.memoriuDepus} onChange={e => set({ memoriuDepus: e.target.checked })} />
          Memoriul de motivare a fost depus
        </label>

        {status.unilateral ? (
          <>
            <div className="tr-divider mt8">Procedura unilaterala</div>
            <p className="f-hint">In procedurile unilaterale, prima instanta poate sa-si revizuiasca propria decizie in termen de {termene.revizuire_luni} luna de la primirea memoriului.</p>
            {status.termenRevizuire && <DeadlineChip label={`Termen revizuire interlocutorie (${termene.revizuire_luni} luna)`} date={status.termenRevizuire} />}
            <label className="check-label mt4">
              <input type="checkbox" checked={r.revizuireInterlocutorie} onChange={e => set({ revizuireInterlocutorie: e.target.checked })} />
              Revizuire interlocutorie acordata de prima instanta
            </label>
          </>
        ) : (
          <>
            <div className="tr-divider mt8">Observatii intimat</div>
            {status.termenObservatii && (
              <DeadlineChip label={`Termen observatii (${termene.observatii_luni} luni)`} date={status.termenObservatii} act={r.pozitie === 'intimat' && !r.observatiiIntimatDepuse} />
            )}
            <div className="field mt4">
              <label>Data depunerii observatiilor</label>
              <DateField value={r.dataObservatii} onChange={v => set({ dataObservatii: v })} />
            </div>
            <label className="check-label">
              <input type="checkbox" checked={r.observatiiIntimatDepuse} onChange={e => set({ observatiiIntimatDepuse: e.target.checked })} />
              Observatiile intimatului au fost depuse
            </label>
          </>
        )}

        <label className="check-label mt8">
          <input type="checkbox" checked={r.recursIncident} onChange={e => set({ recursIncident: e.target.checked })} />
          Recurs incident formulat de intimat
        </label>
        <label className="check-label">
          <input type="checkbox" checked={r.cameraExtinsa} onChange={e => set({ cameraExtinsa: e.target.checked })} />
          Camera extinsa de recurs (Grand Board)
        </label>

        <div className="tr-divider mt8">Decizia camerei</div>
        <div className="f-row">
          <div className="field">
            <label>Solutia camerei de recurs</label>
            <FilterPill value={r.solutieCamera} onChange={e => set({ solutieCamera: e.target.value })}>
              {SOLUTII_CAMERA.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterPill>
          </div>
          <div className="field">
            <label>Data notificarii deciziei camerei</label>
            <DateField value={r.dataDecizieCamera} onChange={v => set({ dataDecizieCamera: v })} />
            <span className="f-hint">Porneste termenul pentru actiunea la Tribunalul UE.</span>
          </div>
        </div>
        <div className="field">
          <label>Cheltuielile de procedura</label>
          <FilterPill value={r.cheltuieliInSarcina} onChange={e => set({ cheltuieliInSarcina: e.target.value })}>
            <option value="">— nestabilite —</option>
            <option value="adversar">In sarcina adversarului</option>
            <option value="noi">In sarcina partii noastre</option>
            <option value="fiecare">Fiecare parte suporta propriile cheltuieli</option>
          </FilterPill>
        </div>
        {status.termenTribunal && !r.tribunalSesizat && (
          <DeadlineChip label={`Termen actiune Tribunalul UE (${termene.tribunal_luni} luni + ${termene.tribunal_zile_distanta} zile distanta)`} date={status.termenTribunal} act />
        )}
      </TrackerCard>

      {/* Tribunalul UE */}
      <TrackerCard title="Tribunalul Uniunii Europene">
        <label className="check-label">
          <input type="checkbox" checked={r.tribunalSesizat} onChange={e => set({ tribunalSesizat: e.target.checked })} />
          Decizia camerei a fost atacata la Tribunalul UE
        </label>
        {r.tribunalSesizat && (
          <div className="dep-section">
            <div className="f-row">
              <div className="field">
                <label>Data introducerii actiunii</label>
                <DateField value={r.dataActiuneTribunal} onChange={v => set({ dataActiuneTribunal: v })} />
              </div>
              <div className="field">
                <label>Nr. cauza T-</label>
                <input type="text" value={r.numarCauzaT} onChange={e => set({ numarCauzaT: e.target.value })} placeholder="T-0123/26" className="mono" />
              </div>
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Pasi procedurali</span>
              <button type="button" className="btn-link" onClick={addEveniment}>+ Adauga eveniment</button>
            </div>
            {(r.evenimente || []).length === 0 ? (
              <p className="f-hint">Niciun pas procedural inregistrat.</p>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap: 4 }}>
                {r.evenimente.map(ev => {
                  const d = parseISO(ev.data);
                  const trecut = d && d < todayMid();
                  return (
                    <div key={ev.id} style={{ display:'flex', alignItems:'center', gap: 8, padding:'6px 8px', border:'1px solid var(--border)', borderRadius: 6, background: trecut ? 'var(--bg-subtle)' : 'transparent' }}>
                      <DateField value={ev.data} onChange={v => setEveniment(ev.id, { data: v })} style={{ width: 130 }} />
                      <input type="text" value={ev.nota} onChange={e => setEveniment(ev.id, { nota: e.target.value })}
                        placeholder="Termen / decizie interlocutorie…" style={{ flex:1, fontSize: 12, border:'none', outline:'none', background:'transparent' }} />
                      <button type="button" className="btn-link" style={{ color:'var(--danger)' }} onClick={() => delEveniment(ev.id)}>✕</button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="f-row mt8">
              <div className="field">
                <label>Solutia Tribunalului UE</label>
                <FilterPill value={r.solutieTribunal} onChange={e => set({ solutieTribunal: e.target.value })}>
                  {SOLUTII_INSTANTA.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </FilterPill>
              </div>
              <div className="field">
                <label>Data hotararii Tribunalului</label>
                <DateField value={r.dataHotarareTribunal} onChange={v => set({ dataHotarareTribunal: v })} />
                <span className="f-hint">Porneste termenul de recurs la CJUE.</span>
              </div>
            </div>
            {status.termenCJUE && !r.cjueSesizata && (
              <DeadlineChip label={`Termen recurs CJUE (${termene.cjue_luni} luni + ${termene.tribunal_zile_distanta} zile distanta)`} date={status.termenCJUE} act />
            )}
          </div>
        )}
      </TrackerCard>

      {/* CJUE */}
      <TrackerCard title="Curtea de Justitie a Uniunii Europene">
        <label className="check-label">
          <input type="checkbox" checked={r.cjueSesizata} onChange={e => set({ cjueSesizata: e.target.checked })} />
          S-a formulat recurs la CJUE
        </label>
        {r.cjueSesizata && (
          <div className="dep-section">
            <div className="f-row">
              <div className="field">
                <label>Data declararii recursului</label>
                <DateField value={r.dataRecursCJUE} onChange={v => set({ dataRecursCJUE: v })} />
              </div>
              <div className="field">
                <label>Nr. cauza C-</label>
                <input type="text" value={r.numarCauzaC} onChange={e => set({ numarCauzaC: e.target.value })} placeholder="C-0123/26" className="mono" />
              </div>
            </div>
            <label className="check-label">
              <input type="checkbox" checked={r.admisFiltrare} onChange={e => set({ admisFiltrare: e.target.checked })} />
              Recursul a trecut procedura de filtrare (admis in principiu)
            </label>
            <div className="field mt4">
              <label>Solutia CJUE</label>
              <FilterPill value={r.solutieCJUE} onChange={e => set({ solutieCJUE: e.target.value })}>
                {SOLUTII_INSTANTA.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </FilterPill>
            </div>
          </div>
        )}
      </TrackerCard>

      {/* Taxe */}
      <TrackerCard title="Taxe si expunere" badge="Anexa I RMUE">
        <table className="fee-table">
          <tbody>
            <FeeRow label="Taxa de recurs" value={taxeCalc.recurs} muted={r.pozitie === 'intimat'} />
          </tbody>
          <tfoot>
            <tr className="fee-total">
              <td>Taxe in sarcina noastra</td>
              <td>{fmtEur(taxeCalc.propria)}</td>
            </tr>
          </tfoot>
        </table>
        <div className="mt8" style={{ background:'var(--bg-subtle)', border:'1px solid var(--border)', borderRadius: 6, padding: '8px 12px' }}>
          <p style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Daca pierdem</p>
          <table className="fee-table">
            <tbody>
              <FeeRow label="Plafon cheltuieli de reprezentare in recurs" value={taxe.plafon_recurs} />
              {r.pozitie === 'intimat' && <FeeRow label="Rambursarea taxei de recurs" value={taxe.recurs} />}
            </tbody>
            <tfoot>
              <tr className="fee-total">
                <td>Expunere maxima estimata</td>
                <td>{fmtEur(taxeCalc.expunere)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <button type="button" className="btn-link mt8" onClick={() => setShowSetari(s => !s)}>
          {showSetari ? '▾' : '▸'} Tarife si termene (editabile)
        </button>
        {showSetari && (
          <div className="tarife-editor">
            <div className="tr-divider">Tarife (EUR)</div>
            {[['recurs','Recurs'],['plafon_recurs','Plafon chelt. recurs'],['plafon_opozitie','Plafon chelt. opozitie'],
              ['plafon_anulare','Plafon chelt. anulare'],['restitutio','Repunere in drepturi'],['continuare','Continuare procedura']
            ].map(([k, l]) => (
              <label key={k} className="tarife-field">{l}
                <input type="number" value={taxe[k]} onChange={e => setTaxe({ ...taxe, [k]: Number(e.target.value)||0 })} />
              </label>
            ))}
            <div className="tr-divider">Termene (luni)</div>
            {[['declaratie_luni','Declaratie + taxa'],['memoriu_luni','Memoriu motivare'],['revizuire_luni','Revizuire interlocutorie'],
              ['observatii_luni','Observatii intimat'],['tribunal_luni','Actiune Tribunal UE'],['cjue_luni','Recurs CJUE']
            ].map(([k, l]) => (
              <label key={k} className="tarife-field">{l}
                <input type="number" value={termene[k]} onChange={e => setTermene({ ...termene, [k]: Number(e.target.value)||0 })} />
              </label>
            ))}
            <div className="tr-divider">Alte termene</div>
            <label className="tarife-field">Zile distanta (tribunal + CJUE)
              <input type="number" value={termene.tribunal_zile_distanta}
                onChange={e => setTermene({ ...termene, tribunal_zile_distanta: Number(e.target.value)||0 })} />
            </label>
          </div>
        )}
      </TrackerCard>

      <TrackerCard title="Observatii interne">
        <textarea className="tr-textarea" value={r.observatii} onChange={e => set({ observatii: e.target.value })}
          rows={3} placeholder="Argumentele recursului, strategia, pozitia adversarului…" />
      </TrackerCard>
    </div>
  );
}

/* ── Sub-components ── */
function AlertBanner({ status }) {
  const { alert } = status;
  return (
    <div className={`tr-alert sev-${alert.sev}`}>
      <strong>{alert.label}</strong>
      {alert.date && (
        <span className="tr-alert-date">
          Termen: {fmtRO(alert.date)}
          {typeof alert.daysLeft === 'number' && (
            alert.daysLeft < 0 ? ` · depasit cu ${Math.abs(alert.daysLeft)} zile` : ` · ${alert.daysLeft} zile ramase`
          )}
        </span>
      )}
    </div>
  );
}

function Timeline({ steps }) {
  const activeIdx = steps.findIndex(s => !s.done);
  return (
    <div className="tr-timeline">
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        const done = s.done;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className={`tl-line${done || steps[i-1].done ? ' tl-line-done' : ''}`} />}
            <div className="tl-step">
              <span className={`tl-dot${done ? ' done' : isActive ? ' active' : ''}`}>{done ? '✓' : i+1}</span>
              <span className={`tl-label${isActive ? ' active' : done ? ' done' : ''}`}>{s.label}</span>
              {s.date && <span className="tl-date">{fmtRO(s.date)}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TrackerCard({ title, badge, children }) {
  return (
    <div className="tr-card">
      <div className="tr-card-head">
        <span className="tr-card-title">{title}</span>
        {badge && <span className="tr-badge">{badge}</span>}
      </div>
      <div className="tr-card-body">{children}</div>
    </div>
  );
}

function SegControl({ value, onChange, options }) {
  return (
    <div className="seg-ctrl">
      {options.map(([v, label]) => (
        <button key={v} type="button" className={`seg-btn${value === v ? ' seg-active' : ''}`}
          onClick={() => onChange(v)}>{label}</button>
      ))}
    </div>
  );
}

function FeeRow({ label, value, muted }) {
  return (
    <tr className={muted ? 'fee-muted' : ''}>
      <td>{label}</td>
      <td className="fee-val">{fmtEur(value)}</td>
    </tr>
  );
}

function DeadlineChip({ label, date, act }) {
  if (!date) return null;
  const dl = daysLeft(date);
  const overdue = dl < 0;
  const soon = dl >= 0 && dl <= 14;
  const cls = overdue ? 'chip-overdue' : (act && soon) ? 'chip-act' : 'chip-neutral';
  return (
    <div className={`deadline-chip ${cls}`}>
      <span>{label}</span>
      <span>{fmtRO(date)} · {overdue ? `${Math.abs(dl)}z depasit` : `${dl}z`}</span>
    </div>
  );
}
